import { useState } from "preact/hooks";

import { api } from "@/api/client";
import type {
  EstimateCaps,
  EstimateResponse,
  PersonCostStats,
  PricedModel,
  ScenarioEstimate,
} from "@/api/types";
import { PanelHelp } from "@/components/PanelHelp";
import { Staleness } from "@/components/Staleness";
import { usePoll } from "@/hooks/usePoll";
import { t, type TranslationKey } from "@/i18n";
import {
  formatDecimal,
  formatInt,
  formatPercent,
  formatUsd,
  formatUsdFine,
  modeLabel,
} from "@/lib/format";
import { includeTestSessions, selectedTenant } from "@/state/filters";

const SEGMENT_LABEL: Record<PersonCostStats["segment"], TranslationKey> = {
  all: "estimate.segment.all",
  anonymous: "estimate.segment.anonymous",
  signed_in: "estimate.segment.signed_in",
};

const SCENARIO_LABEL: Record<ScenarioEstimate["scenario"], TranslationKey> = {
  defensive: "estimate.scenario.defensive",
  normal: "estimate.scenario.normal",
  offensive: "estimate.scenario.offensive",
};

const BASIS_LABEL: Record<ScenarioEstimate["basis"], TranslationKey> = {
  p25: "estimate.basis.p25",
  mean: "estimate.basis.mean",
  p90: "estimate.basis.p90",
};

// Provider names are brands, the same in both languages.
const PROVIDER_LABEL: Record<PricedModel["provider"], string> = {
  claude: "Claude",
  gemini: "Gemini",
};

// The limits an auth_gate_hit names, in the backend's order.
const LIMIT_REASONS: readonly { reason: string; label: TranslationKey }[] = [
  { reason: "questions", label: "estimate.limitReason.questions" },
  { reason: "cost", label: "estimate.limitReason.cost" },
  { reason: "daily_limit", label: "estimate.limitReason.daily_limit" },
];

// The days a scenario's month has: what the daily budget is multiplied by.
const MONTH_DAYS = 30;

const DEFAULT_PEOPLE = 1000;

const usdOrDash = (usd: number | null) => (usd === null ? "—" : formatUsdFine(usd));

const modelLabel = (models: readonly PricedModel[], id: string | undefined) =>
  models.find((m) => m.model_id === id)?.label ?? id ?? "—";

/** The priced models grouped by provider, in the pricing table's order. */
function byProvider(models: readonly PricedModel[]) {
  const groups = new Map<PricedModel["provider"], PricedModel[]>();
  for (const m of models) groups.set(m.provider, [...(groups.get(m.provider) ?? []), m]);
  return [...groups.entries()];
}

export function EstimateView() {
  const [people, setPeople] = useState(DEFAULT_PEOPLE);
  const [models, setModels] = useState<Record<string, string>>({});
  const [caps, setCaps] = useState(false);
  const { data, loading, error, lastFetched, refetch } = usePoll(
    (s) =>
      api.estimate(
        {
          people,
          models,
          caps,
          includeTest: includeTestSessions.value,
          tenant: selectedTenant.value,
        },
        s,
      ),
    [people, JSON.stringify(models), caps, includeTestSessions.value, selectedTenant.value],
  );

  return (
    <div style="display: grid; gap: 24px;">
      <div style="display: flex; align-items: center; gap: 24px;">
        <Staleness lastFetched={lastFetched} loading={loading} error={error} onRefresh={refetch} />
      </div>
      {data?.complete === false && data.read_limit !== undefined && (
        <div class="kpi-sub limit-warn">
          {t("estimate.incomplete", { n: formatInt(data.read_limit) })}
        </div>
      )}
      {data && <PerPerson data={data} />}
      {data && <Limits data={data} />}
      {data && (
        <Scenarios
          data={data}
          people={people}
          models={models}
          caps={caps}
          onApply={(nextPeople, nextModels, nextCaps) => {
            setPeople(nextPeople);
            setModels(nextModels);
            setCaps(nextCaps);
          }}
        />
      )}
      {data && <Profiles data={data} />}
      <div class="kpi-sub">{t("estimate.basisNote")}</div>
    </div>
  );
}

function PerPerson({ data }: { data: EstimateResponse }) {
  return (
    <section class="card">
      <h2>{t("estimate.perPerson")}</h2>
      <PanelHelp k="estimate.perPerson" />
      <table class="table">
        <thead>
          <tr>
            <th>{t("estimate.col.segment")}</th>
            <th>{t("estimate.col.people")}</th>
            <th>{t("estimate.col.sessionsPerPerson")}</th>
            <th>{t("estimate.col.turnsPerPerson")}</th>
            <th>{t("estimate.col.mean")}</th>
            <th>{t("estimate.col.p25")}</th>
            <th>{t("estimate.col.median")}</th>
            <th>{t("estimate.col.p90")}</th>
          </tr>
        </thead>
        <tbody>
          {data.per_person.map((s) => (
            <tr key={s.segment} class={s.segment === "all" ? "row-group" : "row-member"}>
              <td>{t(SEGMENT_LABEL[s.segment])}</td>
              <td>{formatInt(s.people)}</td>
              <td>{s.people > 0 ? formatDecimal(s.sessions / s.people) : "—"}</td>
              <td>{s.people > 0 ? formatDecimal(s.turns / s.people) : "—"}</td>
              <td>{formatUsdFine(s.mean_usd)}</td>
              <td>{usdOrDash(s.p25_usd)}</td>
              <td>{usdOrDash(s.median_usd)}</td>
              <td>{usdOrDash(s.p90_usd)}</td>
            </tr>
          ))}
        </tbody>
      </table>
      <div class="kpi-sub">{t("estimate.minPeople", { n: formatInt(data.min_people) })}</div>
    </section>
  );
}

/** Who reached a limit at least once, per group; above the warning share with
 * enough people the limits deserve a look. An older backend sends none. */
function Limits({ data }: { data: EstimateResponse }) {
  if (data.per_person.every((s) => s.limit_hit === undefined)) return null;
  const warnShare = formatPercent(data.limit_warn_share ?? 0.1);
  return (
    <section class="card">
      <h2>{t("estimate.limits")}</h2>
      <PanelHelp k="estimate.limits" />
      <table class="table">
        <thead>
          <tr>
            <th>{t("estimate.col.segment")}</th>
            <th>{t("estimate.col.people")}</th>
            <th>{t("estimate.col.limitHit")}</th>
            <th>{t("estimate.col.limitShare")}</th>
            {LIMIT_REASONS.map(({ reason, label }) => (
              <th key={reason}>{t(label)}</th>
            ))}
          </tr>
        </thead>
        <tbody>
          {data.per_person.map((s) => (
            <tr key={s.segment} class={s.segment === "all" ? "row-group" : "row-member"}>
              <td>{t(SEGMENT_LABEL[s.segment])}</td>
              <td>{formatInt(s.people)}</td>
              <td>{formatInt(s.limit_hit ?? 0)}</td>
              <td class={s.limit_warning ? "limit-warn" : undefined}>
                {s.limit_hit_share == null ? "—" : formatPercent(s.limit_hit_share)}
                {s.limit_warning && ` · ${t("estimate.limitWarning", { share: warnShare })}`}
              </td>
              {LIMIT_REASONS.map(({ reason }) => (
                <td key={reason}>{formatInt(s.limit_reasons?.[reason] ?? 0)}</td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
      <div class="kpi-sub">
        {t("estimate.limitNote", { share: warnShare, n: formatInt(data.min_people) })}
      </div>
      <div class="kpi-sub">
        {t("estimate.signedInAfterLimit", {
          n: formatInt(data.signed_in_after_free_limit ?? 0),
        })}
      </div>
    </section>
  );
}

const capText = (usd: number) => (usd > 0 ? formatUsd(usd) : t("estimate.capOff"));

/** The limits the costs were bounded by, as one line. */
function CapsInForce({ caps }: { caps: EstimateCaps }) {
  return (
    <div class="kpi-sub">
      {t("estimate.capsInForce", {
        anonymous: capText(caps.anonymous_session_usd),
        signedIn: capText(caps.signed_in_daily_usd),
        daily: capText(caps.daily_usd),
      })}
      {caps.daily_usd > 0 &&
        ` ${t("estimate.capsMonth", { month: formatUsd(caps.daily_usd * MONTH_DAYS) })}`}
    </div>
  );
}

function Scenarios({
  data,
  people,
  models,
  caps,
  onApply,
}: {
  data: EstimateResponse;
  people: number;
  models: Record<string, string>;
  caps: boolean;
  onApply: (people: number, models: Record<string, string>, caps: boolean) => void;
}) {
  const [draftPeople, setDraftPeople] = useState(String(people));
  const [draftModels, setDraftModels] = useState(models);
  const [draftCaps, setDraftCaps] = useState(caps);
  // The backend lists a choice only for the modes it can price with another
  // model; a retired mode id still in the window keeps its profile row only.
  const modes = data.profiles.map((p) => p.mode).filter((mode) => mode in data.chosen_models);
  const groups = byProvider(data.models);

  const submit = (e: Event) => {
    e.preventDefault();
    const n = draftPeople.trim() === "" ? Number.NaN : Number(draftPeople);
    onApply(Number.isFinite(n) && n >= 0 ? Math.round(n) : people, draftModels, draftCaps);
  };

  return (
    <section class="card">
      <h2>{t("estimate.scenarios")}</h2>
      <PanelHelp k="estimate.scenarios" />
      <form
        class="toolbar"
        style="flex-wrap: wrap; gap: 12px; margin-bottom: var(--space-3);"
        onSubmit={submit}
      >
        <label class="toggle">
          <span>{t("estimate.peoplePerMonth")}</span>
          <input
            type="number"
            min={0}
            max={10_000_000}
            step={1}
            required
            value={draftPeople}
            onInput={(e) => setDraftPeople((e.target as HTMLInputElement).value)}
          />
        </label>
        {modes.map((mode) => (
          <label class="toggle" key={mode}>
            <span>{modeLabel(mode)}</span>
            <select
              value={draftModels[mode] ?? data.chosen_models[mode]}
              onInput={(e) =>
                setDraftModels({
                  ...draftModels,
                  [mode]: (e.target as HTMLSelectElement).value,
                })
              }
            >
              {groups.map(([provider, rows]) => (
                <optgroup key={provider} label={PROVIDER_LABEL[provider]}>
                  {rows.map((m) => (
                    <option key={m.model_id} value={m.model_id}>
                      {m.label}
                    </option>
                  ))}
                </optgroup>
              ))}
            </select>
          </label>
        ))}
        <label class="toggle">
          <input
            type="checkbox"
            checked={draftCaps}
            onChange={(e) => setDraftCaps((e.target as HTMLInputElement).checked)}
          />
          <span>{t("estimate.caps")}</span>
        </label>
        <button type="submit" class="window-pill active">
          {t("estimate.apply")}
        </button>
      </form>
      {data.caps && <CapsInForce caps={data.caps} />}

      {data.scenarios.length === 0 ? (
        <div class="kpi-sub">{t("estimate.tooFewPeople", { n: formatInt(data.min_people) })}</div>
      ) : (
        <>
          <div class="grid grid-3">
            {data.scenarios.map((s) => (
              <div key={s.scenario}>
                <h3>{t(SCENARIO_LABEL[s.scenario])}</h3>
                <div class="kpi">{formatUsd(s.usd_per_month)}</div>
                <div class="kpi-sub">
                  {t("estimate.perMonthSub", {
                    v: formatUsdFine(s.usd_per_person),
                    basis: t(BASIS_LABEL[s.basis]),
                  })}
                </div>
                {s.budget_bound && <div class="kpi-sub limit-warn">{t("estimate.budgetBound")}</div>}
              </div>
            ))}
          </div>
          <h3>{t("estimate.normalByMode")}</h3>
          <table class="table">
            <thead>
              <tr>
                <th>{t("common.mode")}</th>
                <th>{t("common.model")}</th>
                <th>{t("estimate.col.perPerson")}</th>
                <th>{t("estimate.col.perMonth")}</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(data.normal_usd_per_mode).map(([mode, usd]) => (
                <tr key={mode}>
                  <td>{modeLabel(mode)}</td>
                  <td>{modelLabel(data.models, data.chosen_models[mode])}</td>
                  <td>{formatUsdFine(usd)}</td>
                  <td>{formatUsd(usd * data.people_per_month)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </section>
  );
}

function Profiles({ data }: { data: EstimateResponse }) {
  return (
    <section class="card">
      <h2>{t("estimate.profiles")}</h2>
      <PanelHelp k="estimate.profiles" />
      <table class="table">
        <thead>
          <tr>
            <th>{t("common.mode")}</th>
            <th>{t("common.model")}</th>
            <th>{t("common.turns")}</th>
            <th>{t("estimate.col.people")}</th>
            <th>{t("estimate.col.inputPerTurn")}</th>
            <th>{t("estimate.col.outputPerTurn")}</th>
            <th>{t("estimate.col.cacheReadPerTurn")}</th>
            <th>{t("estimate.col.cacheWritePerTurn")}</th>
            <th>{t("estimate.col.usdPerTurn")}</th>
          </tr>
        </thead>
        <tbody>
          {data.profiles.map((p) => (
            <tr key={p.mode}>
              <td>{modeLabel(p.mode)}</td>
              <td>{p.model_label || p.model || "—"}</td>
              <td>{formatInt(p.turns)}</td>
              <td>{formatInt(p.people)}</td>
              <td>{formatInt(Math.round(p.input_tokens_per_turn))}</td>
              <td>{formatInt(Math.round(p.output_tokens_per_turn))}</td>
              <td>{formatInt(Math.round(p.cache_read_tokens_per_turn))}</td>
              <td>{formatInt(Math.round(p.cache_write_tokens_per_turn))}</td>
              <td>{formatUsdFine(p.usd_per_turn)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </section>
  );
}
